import React, { FC, useRef, useEffect, forwardRef, useState } from 'react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

interface Props {
  label?: string
  disabled?: boolean
  value?: any
  change?: (value: any) => void
  height?: number
  placeholder?: string
  name?: string
  focus?: boolean
  smalltext?: string
  blur?: () => void
  onAction?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  alarm?: boolean
  maxLength?: number
  required?: boolean
}

const Editor = forwardRef(
  (
    {
      defaultValue,
      onTextChange,
      onSelectionChange,
      change,
      height,
      maxLength,
      onCharCountChange,
    }: any,
    ref: any
  ) => {
    const containerRef = useRef<any>(null)

    useEffect(() => {
      const container: any = containerRef.current
      if (!container) return

      // Limpa instâncias anteriores do DOM se houver
      container.innerHTML = '<div id="editor-container"></div>'
      const editorContainer = container.querySelector('#editor-container')

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['link'],
            [{ color: [] }, { background: [] }],
          ],
        },
      })

      ref.current = quill

      if (defaultValue) {
        quill.clipboard.dangerouslyPasteHTML(defaultValue)
      }

      quill.on('text-change', (delta, oldDelta, source) => {
        onTextChange?.(delta, oldDelta, source)

        let plainText = quill.getText() || ''
        if (plainText.endsWith('\n')) {
          plainText = plainText.slice(0, -1)
        }

        if (onCharCountChange) {
          onCharCountChange(plainText.length)
        }

        if (maxLength && plainText.length > maxLength) {
          quill.deleteText(maxLength, quill.getLength())
          const newContent = quill.root.innerHTML
          change?.(newContent)
          return
        }

        change?.(quill.root.innerHTML)
      })

      quill.on('selection-change', (range, oldRange, source) => {
        onSelectionChange?.(range, oldRange, source)
      })

      return () => {
        ref.current = null
        container.innerHTML = ''
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxLength])

    return (
      <div
        style={{
          height: height ?? 200,
          marginBottom: '4rem',
        }}
        ref={containerRef}
      />
    )
  }
)

const EditorQuill: FC<Props> = (props) => {
  const { label, value, change, height, alarm, maxLength, required } = props

  const [, setRange] = useState()
  const [, setLastChange] = useState()
  const [charCount, setCharCount] = useState(0)
  const quillRef = useRef()

  return (
    <div className='mt-4' style={{ border: alarm ? '1px solid #d90429' : 'none' }}>
      {label && (
        <label className={`form-label fw-normal ${required ? 'required' : ''}`}>{label}</label>
      )}
      <Editor
        ref={quillRef}
        defaultValue={value}
        onSelectionChange={setRange}
        onTextChange={setLastChange}
        change={(val: any) => change && change(val)}
        height={height}
        maxLength={maxLength}
        onCharCountChange={(count: number) => setCharCount(count)}
      />
      {maxLength && (
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#666' }}>
          {charCount} / {maxLength}
        </div>
      )}
    </div>
  )
}

export { EditorQuill }